import { useMemo } from "react";
import ReactQuill, { Quill } from "react-quill";
import BlotFormatter from "quill-blot-formatter";
import PropTypes from "prop-types";


if (
  typeof Quill?.find === "function" &&
  typeof Quill?.prototype?.find !== "function"
) {
  Quill.prototype.find = Quill.find;
}

Quill.register("modules/blotFormatter", BlotFormatter);


const BaseImageFormat = Quill.import("formats/image");
class StyledImage extends BaseImageFormat {
  static create(value) {
    const node = super.create(value);
    if (typeof value === "string") {
      node.setAttribute("src", value);
    } else if (typeof value === "object") {
      if (value.src) node.setAttribute("src", value.src);
      if (value.width) node.setAttribute("width", value.width);
      if (value.height) node.setAttribute("height", value.height);
      if (value.style) node.setAttribute("style", value.style);
      if (value["data-align"]) node.setAttribute("data-align", value["data-align"]);
    }
    return node;
  }

  static value(node) {
    const value = super.value(node);
    if (typeof value === "string") {
      return {
        src: value,
        width: node.getAttribute("width"),
        height: node.getAttribute("height"),
        style: node.getAttribute("style"),
        "data-align": node.getAttribute("data-align"),
      };
    }
    return value;
  }
}
StyledImage.blotName = "image";
StyledImage.tagName = "img";
Quill.register(StyledImage, true);


export function normalizeHtmlValue(value) {
  return typeof value === "string" ? value : "";
}

export function getEditorMetrics(html) {
  const safeHtml = normalizeHtmlValue(html);
  const text = safeHtml
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const wordCount = text ? text.split(" ").filter(Boolean).length : 0;
  const imageCount = (safeHtml.match(/<img\b[^>]*>/g) || []).length;
  const linkCount = (safeHtml.match(/<a\b[^>]*>/g) || []).length;
  const isEmpty = wordCount === 0 && imageCount === 0 && linkCount === 0;

  return { wordCount, imageCount, linkCount, isEmpty };
}


export const quillFormats = [
  "bold",
  "italic",
  "underline",
  "strike",
  "link",
  "list",
  "bullet",
  "image",
  "width",
  "height",
  "align",
  "size",
  "video",
  "blockquote",
  "indent",
  "direction",
  "style",
  "data-align",
];

export function getQuillModules(Instance) {
  return {
    toolbar: {
      container: [
        ["bold", "italic", "underline", "strike"],
        ["blockquote"],
        ["link", "image", "video"],
        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ direction: "rtl" }],
        [{ size: ["small", false, "large", "huge"] }],
        [{ align: [] }],
      ],
      handlers: {
        image: function () {
          const input = document.createElement("input");
          input.setAttribute("type", "file");
          input.setAttribute("accept", "image/*");
          input.click();

          input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append("fileUpload", file);

            try {
              const response = await Instance.post(
                "site/upload/documents",
                formData,
                {
                  headers: { "Content-Type": "multipart/form-data" },
                }
              );

              if (response.data.resCode === "200") {
                const url = "https://pr.webandapi.com" + response.data.documentUrl;
                const range = this.quill.getSelection();
                this.quill.insertEmbed(range.index, "image", url);
              } else {
                console.error("Image upload failed:", response.data);
              }
            } catch (error) {
              console.error("Error uploading image:", error);
            }
          };
        },
      },
    },
    blotFormatter: {},
  };
}

export function PRContentPreview({
  headline,
  bodyHtml,
  wordLimit,
  imgLimit,
  linkLimit,
}) {
  const { isEmpty } = useMemo(
    () => getEditorMetrics(bodyHtml),
    [bodyHtml]
  );

  const safeHeadline =
    typeof headline === "string" ? headline.trim() : "";
  const safeBodyHtml = normalizeHtmlValue(bodyHtml);

  return (
    <section
      aria-label="Press release preview"
      className="mb-4 border shadow rounded p-4 position-relative"
    >
      <span
        className="text-secondary fw-semibold bg-light px-2"
        style={{ fontSize: "0.75rem", position: "absolute", top: 1, left: 1 }}
      >
        Preview
      </span>
      <div className="mb-4">
        <div
          className="submit_form_control fs-4 fw-bold"
          role="textbox"
          aria-readonly="true"
        >
          {safeHeadline ? (
            safeHeadline
          ) : (
            <span className="text-muted">No headline provided.</span>
          )}
        </div>
      </div>
      <div>
        <div className="custom-ckeditor-wrapper pr-preview">
          {isEmpty && (
            <div className="text-muted mb-2" role="status" aria-live="polite">
              No press release body to preview.
            </div>
          )}
          <ReactQuill
            value={safeBodyHtml}
            modules={{ toolbar: false }}
            formats={quillFormats}
            bounds={".custom-ckeditor-wrapper"}
            readOnly
            theme="snow"
          />
        </div>
      </div>
    </section>
  );
}

PRContentPreview.propTypes = {
  headline: PropTypes.string,
  bodyHtml: PropTypes.string,
  wordLimit: PropTypes.number.isRequired,
  imgLimit: PropTypes.number.isRequired,
  linkLimit: PropTypes.number.isRequired,
};
